/**
 * CreateInnerLinks
 * ページ内の指定要素から自動的にページ内リンク一覧を生成
 * IDが設定されていない要素には、テキスト内容から自動的にIDを生成する
 * 
 * @example
 * HTML（data-target指定の場合）:
 * <div class="js-create-innerlinks p-innerlinks" data-target="cont-guideline" data-elm="h3">
 *   <!-- ここにページ内リンク一覧が生成される -->
 * </div>
 * <div id="cont-guideline">
 *   <h3 id="overview">概要</h3>
 *   <h3>目的</h3>  <!-- IDが無くても自動生成される -->
 * </div>
 * 
 * HTML（次要素をターゲットにする場合、data-target未指定時のデフォルト動作）:
 * <div class="js-create-innerlinks p-innerlinks" data-elm="h3">
 *   <!-- ここにページ内リンク一覧が生成される -->
 * </div>
 * <div>
 *   <h3>概要</h3>  <!-- この要素内のh3が対象になる -->
 *   <h3>使い方</h3>
 * </div>
 *
 * HTML（離れた複数セクションを指定する場合＝data-targets。各セクションへのリンクを順に生成）:
 * <nav class="js-create-innerlinks p-innerlinks" data-targets="#service-list,#mindmap,#stats" data-elm=".c-heading .ja">
 *   <!-- 各セクションの見出し(data-elm)を文言に、セクションのid(無ければ自動付与)へリンク -->
 * </nav>
 *   ※data-elm はセクション内の「文言を取る要素」のセレクタ（既定 .c-heading .ja → 無ければ h2,h3,h4）。
 *     リンク先はセクション自身（見出しではなくセクション先頭へスクロール）。
 *
 * JS: new CreateInnerLinks();
 */
export default class CreateInnerLinks {
  constructor(options = {}) {
    this.options = {
      selector: '.js-create-innerlinks',
      defaultElm: 'h3',
      ...options
    };

    this.init();
  }

  init() {
    const elements = document.querySelectorAll(this.options.selector);
    if (elements.length === 0) return;

    elements.forEach(element => {
      // コードハイライトブロック内の要素は処理しない
      if (element.closest('.js-highlight') || element.closest('pre') || element.closest('code')) {
        return;
      }
      this.processElement(element);
    });
  }

  processElement(element) {
    // data-targets（離れた複数セクション）モード
    const targetsAttr = element.getAttribute('data-targets');
    if (targetsAttr) {
      this.processTargets(element, targetsAttr);
      return;
    }

    const targetId = element.getAttribute('data-target');
    let targetElement;

    if (targetId) {
      // data-target属性が指定されている場合はIDで検索
      targetElement = document.getElementById(targetId);
      if (!targetElement) return;
    } else {
      // data-target属性がない場合は次の要素をターゲットにする
      targetElement = element.nextElementSibling;
      if (!targetElement) return;
    }

    // data-elm属性で指定されたセレクタを取得、なければデフォルト（h3）
    const elmSelector = element.getAttribute('data-elm') || this.options.defaultElm;
    
    // すべての対象要素を取得（IDの有無は問わない）
    let targetSelector;
    if (elmSelector.startsWith('.') || elmSelector.startsWith('#')) {
      // クラス名またはID指定の場合はそのまま使用
      targetSelector = elmSelector;
    } else {
      // タグ名の場合
      targetSelector = elmSelector;
    }
    
    const allTargetElements = targetElement.querySelectorAll(targetSelector);
    
    // js-highlight内の要素を除外
    const targetElements = Array.from(allTargetElements).filter(el => {
      return !el.closest('.js-highlight') && !el.closest('pre') && !el.closest('code');
    });
    
    if (targetElements.length === 0) return;

    // IDが無い要素に自動でIDを付与
    this.ensureElementIds(targetElements);

    // 既存のulを削除（再生成の場合）
    const existingUl = element.querySelector('ul');
    if (existingUl) {
      existingUl.remove();
    }

    // ページ内リンク一覧を生成
    const ul = this.createLinksList(targetElements);
    element.appendChild(ul);
  }

  /**
   * data-targets モード：カンマ区切りのセクションセレクタを順に走査し、
   * 各セクションの見出し(data-elm)を文言に、セクションid（無ければ自動付与）へのリンクを生成する。
   */
  processTargets(element, targetsAttr) {
    const selectors = targetsAttr.split(',').map((s) => s.trim()).filter(Boolean);
    const labelSelector = element.getAttribute('data-elm') || '.c-heading .ja';

    const entries = [];
    selectors.forEach((sel) => {
      let section;
      try {
        section = document.querySelector(sel);
      } catch (e) {
        return;
      }
      if (!section) return;

      const labelEl = section.querySelector(labelSelector) || section.querySelector('h2, h3, h4');
      const text = (labelEl ? labelEl.textContent : '').trim();
      if (!text) return;

      // リンク先＝セクション自身。id が無ければ文言から自動付与
      if (!section.id) {
        section.id = this.ensureUniqueId(this.generateIdFromText(text, entries.length));
      }
      entries.push({ id: section.id, text });
    });

    if (entries.length === 0) return;

    const existingUl = element.querySelector('ul');
    if (existingUl) existingUl.remove();

    const ul = document.createElement('ul');
    entries.forEach(({ id, text }) => {
      const li = document.createElement('li');
      const a = document.createElement('a');
      a.href = `#${id}`;
      a.textContent = text;
      li.appendChild(a);
      ul.appendChild(li);
    });
    element.appendChild(ul);
  }

  ensureElementIds(elements) {
    elements.forEach((element, index) => {
      if (!element.getAttribute('id')) {
        // テキストからID生成
        const spanElement = element.querySelector('span');
        const text = spanElement ? spanElement.textContent.trim() : element.textContent.trim();
        
        // IDに使える文字列に変換
        let id = this.generateIdFromText(text, index);
        
        // 重複チェック
        id = this.ensureUniqueId(id);
        
        element.setAttribute('id', id);
      }
    });
  }

  generateIdFromText(text, index) {
    // 基本的な文字列変換
    let id = text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // 英数字、スペース、ハイフン以外を削除
      .replace(/\s+/g, '-') // スペースをハイフンに
      .replace(/^-+|-+$/g, '') // 先頭末尾のハイフンを削除
      .substring(0, 50); // 長さ制限
    
    // 空の場合や数字で始まる場合はフォールバック
    if (!id || /^\d/.test(id)) {
      id = `heading-${index + 1}`;
    }
    
    // 数字で始まる場合は頭に'h'を付ける
    if (/^\d/.test(id)) {
      id = `h${id}`;
    }
    
    return id;
  }

  ensureUniqueId(baseId) {
    let id = baseId;
    let counter = 1;
    
    // 重複がある限りカウンターを追加
    while (document.getElementById(id)) {
      id = `${baseId}-${counter}`;
      counter++;
    }
    
    return id;
  }

  createLinksList(elements) {
    const ul = document.createElement('ul');

    elements.forEach(element => {
      const id = element.getAttribute('id');
      
      // spanタグがある場合はspanの内容、なければ要素全体のテキスト
      const spanElement = element.querySelector('span');
      const text = spanElement ? spanElement.textContent.trim() : element.textContent.trim();

      if (id && text) {
        const li = document.createElement('li');
        const a = document.createElement('a');

        a.href = `#${id}`;
        a.textContent = text;

        li.appendChild(a);
        ul.appendChild(li);
      }
    });

    return ul;
  }

  // 動的にページ内リンクを再生成
  refresh() {
    this.init();
  }

  // 特定の要素のみ再生成
  refreshElement(element) {
    if (element && element.matches(this.options.selector)) {
      this.processElement(element);
    }
  }
}